"use client";

import { useEffect, useState } from "react";

import { isEditing } from "@builder.io/sdk-react";
import type { RegisteredComponent } from "@builder.io/sdk-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/hec/components/ui/dialog";
import {
  isEngagementStallSuppressed,
  suppressEngagementStall,
} from "~/hec/telehealth/engagement-stall-storage";
import {
  isTreatmentAvailable,
  treatmentBySlug,
} from "~/hec/telehealth/treatments";
import {
  type EngagementStallTriggerType,
  useEngagementStallTriggers,
} from "~/hec/telehealth/use-engagement-stall-triggers";
import { useStartIntake } from "~/hec/telehealth/use-start-intake";

interface EngagementStallModalProps {
  /** Builder injects this on `noWrap` components — must spread on the root. */
  attributes?: Record<string, unknown>;
  treatment?: string;
  headline?: string;
  body?: string;
  pricePerDay?: string;
  ctaLabel?: string;
}

const DISCLAIMER =
  "Prescription treatment requires provider review and is not guaranteed.";

/**
 * Price line + CTA row + disclaimer, shared verbatim between the live Dialog
 * and the Builder editor preview so the two can't drift (an ET-137 copy or
 * button change lands once, in both). `preview` disables the actions and
 * skips the `DialogClose` wrapper (no Dialog context in the editor branch).
 */
function StallActionsAndDisclaimer({
  pricePerDay,
  ctaLabel,
  preview = false,
  onGetStarted,
}: {
  pricePerDay: string;
  ctaLabel: string;
  preview?: boolean;
  onGetStarted?: () => void;
}) {
  const notNowButton = (
    <Button
      variant="outline"
      size="5"
      disabled={preview}
      data-testid="engagement-stall-not-now-button"
    >
      Not now
    </Button>
  );
  return (
    <>
      <p
        className="text-sm font-semibold text-deep-blue"
        data-testid="engagement-stall-price"
      >
        {pricePerDay}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          variant="cta"
          size="5"
          disabled={preview}
          onClick={onGetStarted}
          data-testid="engagement-stall-get-started-button"
        >
          {ctaLabel}
        </Button>
        {preview ? (
          notNowButton
        ) : (
          <DialogClose asChild>{notNowButton}</DialogClose>
        )}
      </div>
      <p
        className="text-xs text-neutral-9"
        data-testid="engagement-stall-disclaimer"
      >
        {DISCLAIMER}
      </p>
    </>
  );
}

function applyCopyTokens(
  text: string,
  treatmentLabel: string,
  pricePerDay: string,
): string {
  return text
    .replaceAll("[Treatment Name]", treatmentLabel)
    .replaceAll("[price per day]", pricePerDay);
}

/**
 * ET-109 — Patiently treatment-landing "engagement stall" CTA modal. Authors
 * drop this Builder block on any treatment landing page; a page counts as
 * "supported" simply by having the block on it (ET-11's dedicated landing
 * page doesn't exist in the Workers app yet — this block is not route-gated).
 *
 * Copy (headline/body/price) are Builder inputs defaulting to the ticket's
 * Example Copy with placeholder pricing, so ET-137's real pricing +
 * Legal-approved wording is a content edit in Builder, not a deploy. The
 * eligibility/provider-review disclaimer is hardcoded — compliance text
 * should not be author-editable.
 *
 * Suppression (session- and device-scoped per treatment, Owen Hall
 * 2026-07-08) is recorded the instant the modal is shown — "shown" already
 * subsumes "dismissed" and "clicked this modal's own CTA", so only one
 * storage write happens here. "Already clicked Get Started elsewhere on the
 * page" is handled centrally in `useStartIntake` (ET-12's shared CTA
 * contract), not duplicated here.
 */
export function EngagementStallModal({
  attributes,
  treatment = "weight-loss",
  headline = "Ready to get started with [Treatment Name]?",
  body = "Treatment starts at [price per day]. Complete a secure intake and a licensed provider will review your information to determine whether treatment is appropriate.",
  pricePerDay = "$X.XX per day",
  ctaLabel = "Get Started",
}: EngagementStallModalProps) {
  const startIntake = useStartIntake();
  const [open, setOpen] = useState(false);
  const [triggerType, setTriggerType] =
    useState<EngagementStallTriggerType | null>(null);
  // Defaults to suppressed until the mount effect reads sessionStorage —
  // deterministic first render (no hydration mismatch) and no window where
  // triggers could arm before the suppression check completes.
  const [suppressed, setSuppressed] = useState(true);

  const treatmentDef = treatmentBySlug(treatment);
  const treatmentSlug = treatmentDef?.slug ?? treatment;
  const treatmentLabel = treatmentDef?.label ?? treatment;
  // An unavailable/coming-soon treatment must never stall-prompt: its Get
  // Started would silently no-op (useStartIntake gates availability and
  // returns false) — a dead CTA in an open dialog, plus conversion events
  // for intakes that never started. Same guard as first-question's
  // handleStart.
  const available = isTreatmentAvailable(treatmentSlug);
  const editing = isEditing();

  useEffect(() => {
    setSuppressed(isEngagementStallSuppressed(treatmentSlug));
  }, [treatmentSlug]);

  useEngagementStallTriggers({
    enabled: !editing && !suppressed && !open && available,
    onTrigger: trigger => {
      // Re-check storage synchronously at trigger time: with two instances of
      // this block for the same treatment on one page, both mount with
      // suppressed=false and arm independently — the first to fire writes
      // "shown" below, and this read is what stops the second from stacking a
      // duplicate dialog + duplicate events on top of it.
      if (isEngagementStallSuppressed(treatmentSlug)) {
        setSuppressed(true);
        return;
      }
      setTriggerType(trigger);
      setOpen(true);
      suppressEngagementStall(treatmentSlug, "shown");
      setSuppressed(true);
    },
  });

  const handleGetStarted = () => {
    // ET-12 backstop, same as first-question's handleStart: `startIntake`
    // gates availability internally, but guard here too so a misconfigured
    // block never emits a cta_clicked conversion event for an intake that
    // will not start.
    if (!isTreatmentAvailable(treatmentSlug)) return;
    // ET-12 shared contract: routes to
    // `/intake/[treatment]`. Also writes the generic "already clicked a page
    // CTA" suppression (see use-start-intake.ts) — redundant with the
    // "shown" write above, but harmless (same suppressed:true key).
    startIntake({
      treatment: treatmentSlug,
      source: "engagement-stall-modal",
      ctaLocation: triggerType ?? undefined,
    });
  };

  const resolvedHeadline = applyCopyTokens(
    headline,
    treatmentLabel,
    pricePerDay,
  );
  const resolvedBody = applyCopyTokens(body, treatmentLabel, pricePerDay);

  if (editing) {
    return (
      <div {...attributes} data-testid="engagement-stall-root">
        <div
          className="mx-auto flex w-full max-w-md flex-col gap-3 rounded-2xl border border-blue-03 bg-white p-6 shadow-sm"
          data-testid="engagement-stall-editor-preview"
        >
          {/* Heading/body wrappers can't be shared with the live branch:
              DialogTitle/DialogDescription require Radix Dialog context. The
              strings and everything below them are single-sourced. */}
          <h2 className="font-grotesque text-xl font-bold text-deep-blue">
            {resolvedHeadline}
          </h2>
          <p className="text-sm text-neutral-10">{resolvedBody}</p>
          <StallActionsAndDisclaimer
            pricePerDay={pricePerDay}
            ctaLabel={ctaLabel}
            preview
          />
        </div>
      </div>
    );
  }

  return (
    <div {...attributes} data-testid="engagement-stall-root">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          animation="pop"
          data-testid="engagement-stall-modal"
          className="max-w-md"
        >
          <DialogTitle data-testid="engagement-stall-headline">
            {resolvedHeadline}
          </DialogTitle>
          <DialogDescription data-testid="engagement-stall-body">
            {resolvedBody}
          </DialogDescription>
          <StallActionsAndDisclaimer
            pricePerDay={pricePerDay}
            ctaLabel={ctaLabel}
            onGetStarted={handleGetStarted}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const EngagementStallModalDefinition = {
  component: EngagementStallModal,
  name: "TelehealthEngagementStallModal",
  noWrap: true,
  inputs: [
    {
      name: "treatment",
      type: "string",
      enum: ["weight-loss"],
      defaultValue: "weight-loss",
      helperText: "Treatment this modal's copy and Get Started CTA apply to.",
    },
    {
      name: "headline",
      type: "string",
      defaultValue: "Ready to get started with [Treatment Name]?",
      helperText:
        "Use [Treatment Name] as a token — it's substituted with the treatment's display label.",
    },
    {
      name: "body",
      type: "string",
      defaultValue:
        "Treatment starts at [price per day]. Complete a secure intake and a licensed provider will review your information to determine whether treatment is appropriate.",
      helperText:
        "Use [price per day] as a token — it's substituted with the Price Per Day field below.",
    },
    {
      name: "pricePerDay",
      type: "string",
      defaultValue: "$X.XX per day",
      helperText:
        "Placeholder until approved pricing + Legal sign-off land (ET-137). Update this field directly when they do — no deploy required.",
    },
    { name: "ctaLabel", type: "string", defaultValue: "Get Started" },
    // No `landingPageType` input: it existed only to populate a
    // `landing_page_type` property on the removed Segment events, and the APIM
    // ingest spec has no such field. Authored values on existing entries are
    // simply ignored.
  ],
} satisfies RegisteredComponent;
